import { Images } from 'lucide-react';
import type { UseFormReturn } from 'react-hook-form';

import { FormField, FormInput, FormSelect } from '@/_components';
import { CardContainer, FormFilesInput, FormLayout, SectionHeading, SelectorWithAvatarContainer } from '@/_containers';
import { RichText } from '@/_containers/inputs/rich-text';
import { AsyncCompanySelector } from '@/_pages/companies/shared';
import { BrandsAsyncSelect } from '@/_pages/settings/brands/shared';
import { getMonthOptionsByIndex, getYearOptions } from '@/_schemas/generic';
import { allowedFileTypes, nameResolver, showFormField } from '@/_shared';

import { AwardTypeInput } from './award-type';

import type { AwardFormSchema } from '../schemas';

const monthsOptions = getMonthOptionsByIndex();
const estYearOptions = getYearOptions();

export function FormFields({
  form,
  isEditable = true,
  autoFocus = false,
}: {
  form: UseFormReturn<AwardFormSchema>;
  isEditable?: boolean;
  autoFocus?: boolean;
}) {
  const [award_type, id, notes, gallery] = form.watch(['award_type', 'id', 'notes', 'gallery']);

  return (
    <>
      <CardContainer>
        <FormLayout>
          <FormField
            control={form.control}
            name="name"
            render={({ field: { value, onChange } }) => (
              <FormInput
                formItemClassName="col-span-12 sm:col-span-6 lg:col-span-4"
                label="Award Name (required)"
                value={value}
                onChange={onChange}
                disabled={!isEditable}
                autoFocus={autoFocus}
              />
            )}
          />
          <FormField
            control={form.control}
            name="sponsoring_organization"
            render={({ field: { value, onChange } }) => (
              <FormInput
                formItemClassName="col-span-12 sm:col-span-6 lg:col-span-4"
                label="Organization"
                value={value}
                onChange={onChange}
                disabled={!isEditable}
              />
            )}
          />
          <div className="col-span-12 sm:col-span-6 lg:col-span-4">
            <AwardTypeInput isEditable={isEditable} />
          </div>
          <FormField
            control={form.control}
            name="award_month"
            render={({ field: { value, onChange } }) => (
              <FormSelect
                formItemClassName="col-span-12 sm:col-span-6 lg:col-span-4"
                disabled={!isEditable}
                value={value}
                onChange={onChange}
                options={monthsOptions}
                getOptionLabel={(option) => option.name}
                label="Month"
              />
            )}
          />

          <FormField
            control={form.control}
            name="award_year"
            render={({ field }) => (
              <FormSelect
                formItemClassName="col-span-12 sm:col-span-6 lg:col-span-4"
                disabled={!isEditable}
                value={field?.value}
                onChange={field.onChange}
                options={estYearOptions}
                getOptionLabel={(option) => option.name}
                label="Year"
              />
            )}
          />

          {!award_type ||
            (award_type?.value === 'internal' && (
              <div className="col-span-12 sm:col-span-6 lg:col-span-4">
                <FormField
                  control={form.control}
                  name="brand"
                  render={({ field: { value, onChange } }) => (
                    <SelectorWithAvatarContainer
                      disabled={!isEditable}
                      src={value?.icon?.at(0)?.url || ''}
                      label={nameResolver(value?.name, undefined, 'Brand')}
                      name={value?.name ?? ''}
                      className="lg:flex-row"
                    >
                      <BrandsAsyncSelect disabled={!isEditable} label="Brand" value={value} onValueChange={onChange} />
                    </SelectorWithAvatarContainer>
                  )}
                />
              </div>
            ))}
          {award_type && award_type?.value !== 'internal' && (
            <div className="col-span-12 sm:col-span-6 lg:col-span-4">
              <FormField
                control={form.control}
                name="client"
                render={({ field: { value, onChange } }) => (
                  <SelectorWithAvatarContainer
                    disabled={!isEditable}
                    src={value?.logo?.at(0)?.url || ''}
                    label={value?.name || 'Select Company'}
                    name={value?.name ?? ''}
                    className="lg:flex-row"
                    avatarClassName="w-14 rounded-md"
                  >
                    <AsyncCompanySelector
                      disabled={!isEditable}
                      label="Company"
                      value={value}
                      onValueChange={onChange}
                    />
                  </SelectorWithAvatarContainer>
                )}
              />
            </div>
          )}
        </FormLayout>
      </CardContainer>

      {id !== -1 && (
        <>
          {showFormField(isEditable, notes?.length > 0) && (
            <CardContainer>
              <SectionHeading text="Notes" />
              <FormLayout>
                <FormField
                  control={form.control}
                  name="notes"
                  render={({ field: { value, onChange } }) => (
                    <RichText
                      value={value}
                      onChange={onChange}
                      disabled={!isEditable}
                      inline={!isEditable}
                      mediaFolderPath={`awards/${id}/notes`}
                      className="col-span-12"
                    />
                  )}
                />
              </FormLayout>
            </CardContainer>
          )}

          {showFormField(isEditable, gallery?.length > 0) && (
            <CardContainer>
              <SectionHeading text="Gallery" icon={Images} />

              <FormField
                control={form.control}
                name="gallery"
                render={({ field: { value, onChange } }) => (
                  <FormFilesInput
                    folder_path={`awards/${id}/gallery`}
                    value={value}
                    disabled={!isEditable}
                    onChange={onChange}
                    formItemClassName="mt-6"
                    allowedTypes={allowedFileTypes.all}
                  />
                )}
              />
            </CardContainer>
          )}
        </>
      )}
    </>
  );
}
